import React, { useEffect, useMemo, useState } from "react";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import ViewOODTimesheet from "../../../components/ViewOODTimesheet";
import { showSuccess, showError } from "../../../v2/utils/toast.ts";
import { getApiErrorMessage } from "../../../v2/utils/api-error-message.ts";
import axios from "axios";
import {
  API_LEAVE_GET_HR_OOD_V2_ACTION,
} from "../../../config/Endpoints";
import Cookies from "js-cookie";
import { formatDate, displayFormatDate } from "../../../utils/helpers";
import { putApi } from "../../../utils/api";
import * as Yup from "yup";
import validations from "../../../config/validation";
import { Event } from "../../../config/events";
import { dispatchEvent } from "../../../Hooks/useEvent";
import {
  Button,
  Modal,
  ResponsiveDataTable,
  Select,
  TextArea,
} from "../../../v2/components/ui/index.ts";

function getRmOrPmApproval(approvals) {
  if (!Array.isArray(approvals)) return null;
  return (
    approvals.find((a) => a.approverType === "RM") ||
    approvals.find((a) => a.approverType === "PM") ||
    null
  );
}

const ApproveOODModal = ({ type, timesheet, selectedEntry, onClose, open }) => {
  const [isLoading, setIsLoading] = useState(false);
  let formDataInitialState;
  switch (type) {
    case "DL":
      formDataInitialState = {
        action: "",
        comment: "",
      };
      break;
    case "HR":
      formDataInitialState = {
        action: "",
        comment: "",
        plAmount: "",
      };
      break;
    default:
      showError("Incorrect Configuration, Please contact portal admin.");
  }
  const [formData, setFormData] = useState(formDataInitialState);
  const [totalTime, setTotalTime] = useState({
    hours: "",
    minutes: "",
  });
  const accessToken = Cookies.get("accessToken");
  const [errors, setErrors] = useState({});

  const validationSchema = Yup.object().shape({
    comment: validations.COMMENT_VALIDATION,
  });

  const hrValidationSchema = Yup.object().shape({
    comment: validations.COMMENT_VALIDATION,
    action: Yup.string().required("Action is required"),
    plAmount: Yup.string().when("action", {
      is: "APPROVED",
      then: (schema) =>
        schema
          .required("PL Credit is required")
          .oneOf(["0.5", "1"], "Select PL Credit"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const hrSummaryColumns = useMemo(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: false,
      },
      {
        id: "leaveDate",
        header: "Date",
        accessor: "leaveDate",
        sortable: false,
      },
      {
        id: "reportingManagerName",
        header: "Reporting Manager",
        accessor: "reportingManagerName",
        sortable: false,
      },

    ],
    [],
  );

  const dlSummaryColumns = useMemo(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: false,
      },
      {
        id: "leaveDate",
        header: "On Official Duty-Date",
        accessor: "leaveDate",
        sortable: false,
      },
      {
        id: "dlComment",
        header: "Comment",
        accessor: "dlComment",
        sortable: false,
        truncate: true,
        getTitleText: (row) => String(row.dlComment ?? ""),
      },
    ],
    [],
  );

  const [tableData, setTableData] = useState([]);

  const viewTimesheetForViewOOD = useMemo(() => timesheet || [], [timesheet]);

  useEffect(() => {
    if (!selectedEntry) {
      setTableData([]);
      return;
    }
    if (type === "HR") {
      const appr = getRmOrPmApproval(selectedEntry.approvals);
      const cmt =
        appr?.comment != null && String(appr.comment).trim() !== ""
          ? appr.comment
          : "—";
      setTableData([
        {
          employeeName:
            selectedEntry.employeeName != null &&
              String(selectedEntry.employeeName).trim() !== ""
              ? selectedEntry.employeeName
              : "—",
          leaveDate: displayFormatDate(selectedEntry.date),
          reportingManagerName:
            selectedEntry.reportingManagerName != null &&
              String(selectedEntry.reportingManagerName).trim() !== ""
              ? selectedEntry.reportingManagerName
              : "—",
          dlComment: cmt,
          _original: selectedEntry,
        },
      ]);
      return;
    }
    if (type === "DL") {
      setTableData(
        [selectedEntry]?.map((row) => ({
          employeeName: row.employeeName,
          leaveDate: formatDate(row.startDate),
          dlComment: row.firstApprovalComment ?? "—",
          _original: row,
        })) || [],
      );
    }
  }, [selectedEntry, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setErrors({});

      if (type === "HR") {
        await hrValidationSchema.validate(formData, { abortEarly: false });

        const timesheetId = Number(
          selectedEntry?.id ?? selectedEntry?.timesheetId,
        );
        if (!Number.isFinite(timesheetId)) {
          showError("Missing timesheet id for this request.")
          return;
        }
        const payload = {
          timesheetId,
          leaveCredit:
            formData.action === "APPROVED" ? Number(formData.plAmount) : 0,
          hrComment: formData.comment,
          hrStatus: formData.action,
        };

        await putApi(API_LEAVE_GET_HR_OOD_V2_ACTION, payload, accessToken);

        const status = formData.action === "APPROVED" ? "Approved" : "Rejected";
        showSuccess(`On Official Duty request ${status}`);

        dispatchEvent(Event.SIDEBAR.NOTIFY_EVENT);
        handleReset();
        onClose?.();
      } else {
        await validationSchema.validate(formData, {
          abortEarly: false,
        });

        await putApi(
          `${API_LEAVE_GET_HR_OOD_V2_ACTION}?leaveId=${selectedEntry.laveId}&Status=${formData.action}`,
          { comment: formData.comment },
          accessToken,
        );

        const status = formData.action === "APPROVED" ? "Approved" : "Reject";
        showSuccess(`On Official Duty request ${status}`)
        dispatchEvent(Event.SIDEBAR.NOTIFY_EVENT);
        const modalClose = document.getElementsByClassName("cancel-modal")[0];
        modalClose?.click();

        handleReset();
        onClose?.();
      }
    } catch (error) {
      if (error?.inner) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        showError(error?.response?.data?.message ||
          "Something went wrong please try again later")
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((state) => ({
      ...state,
      [name]: value,
    }));

    setErrors((prevFormData) => ({
      ...prevFormData,
      [name]: "",
    }));
  };

  const handleReset = () => {
    setFormData(formDataInitialState);
    setErrors({});
  };

  const hrOodFormId = "approve-hr-ood-form";

  if (type === "HR") {
    return (
      <Modal
        open={Boolean(open)}
        onClose={() => {
          handleReset();
          onClose?.();
        }}
        title="Approve On Official Duty"
        size="lg"
        closeOnBackdropClick={false}
        closeOnEscape={false}
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap w-100">
            <Button
              type="submit"
              form={hrOodFormId}
              variant="accent"
              disabled={isLoading}
            >
              {isLoading ? (
                <ThreeDots
                  height="16"
                  width="40"
                  radius="9"
                  color="white"
                  ariaLabel="three-dots-loading"
                  visible
                />
              ) : (
                "Submit"
              )}
            </Button>
            <Button
              type="button"
              variant="light-grey"
              onClick={() => {
                handleReset();
                onClose?.();
              }}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <form id={hrOodFormId} onSubmit={handleSubmit} className="position-relative">
          {isLoading ? (
            <div className="overlay">
              <div className="loader-container">
                <TailSpin
                  height="64"
                  width="64"
                  radius="1"
                  color="var(--color-accent-500, #2563eb)"
                  ariaLabel="tail-spin-loading"
                  visible
                />
              </div>
            </div>
          ) : null}
          <div className="d-flex flex-column gap-4">
            <ResponsiveDataTable
              pagination={false}
              columns={hrSummaryColumns}
              data={tableData}
              aria-label="Request summary"
            />
            <div className="d-flex flex-column timesheet pb-2 gap-3">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <h2 className="label fs-6 mb-0">Timesheet</h2>
                <div
                  className="timesheet-total-time d-flex gap-2 align-items-center justify-content-center px-3 py-2 rounded"
                  style={{ backgroundColor: "var(--card-nested-surface, #e5e9f1)" }}
                >
                  <span>Total Time</span>
                  <div className="d-flex">
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ gap: "2px" }}
                    >
                      <span>{totalTime.hours}</span>
                      <span>Hrs</span>
                    </div>
                    {totalTime.minutes > 0 && (
                      <>
                        <span className="d-flex align-items-center justify-content-center px-2">
                          :
                        </span>
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{ gap: "2px" }}
                        >
                          <span>{totalTime.minutes}</span>
                          <span>Mins</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <ViewOODTimesheet
                timesheet={viewTimesheetForViewOOD}
                setTotalTime={setTotalTime}
              />
            </div>

            <div className="d-flex  flex-wrap gap-3">
              <div className="d-flex justify-content-center col-md-12">
                <div className="col-md-2"></div>
                <div className="col-md-7"> <Select
                  labelLayout={"inline"}
                  label={"Action"}
                  value={formData.action}
                  required
                  onChange={(v) => {
                    setFormData((s) => ({ ...s, action: v }));
                    setErrors((e) => ({ ...e, action: "" }));
                  }}
                  options={[
                    { value: "APPROVED", label: "Approve" },
                    { value: "REJECTED", label: "Reject" },
                  ]}
                  placeholder="Select Action"
                  error={errors.action}
                /></div>
                <div className="col-md-2"></div>
              </div>
              <div className="d-flex justify-content-center col-md-12"><div className="col-md-2"></div>
                <div className="col-md-7">
                  {formData.action === "APPROVED" && (
                    <Select
                      labelLayout={"inline"}
                      label="PL Credit"
                      value={formData.plAmount}
                      onChange={(v) => {
                        setFormData((s) => ({ ...s, plAmount: v }));
                        setErrors((e) => ({ ...e, plAmount: "" }));
                      }}
                      options={[
                        { value: "0.5", label: "0.5" },
                        { value: "1", label: "1" },
                      ]}
                      placeholder="Select PL count"
                      error={errors.plAmount}
                    />
                  )}
                </div>
                <div className="col-md-2"></div>
              </div>
              <div className="d-flex justify-content-center col-md-12"><div className="col-md-2"></div>
                <div className="col-md-7">
                  <TextArea
                    labelLayout={"inline"}
                    label="Comment"
                    id="hr-ood-comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    rows={3}
                    required
                    error={errors.comment}
                  />
                </div>
                <div className="col-md-2"></div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {isLoading && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="tail-spin-loading"
              wrapperStyle={{}}
              wrapperClassName=""
              visible={true}
            />
          </div>
        </div>
      )}
      <div className="modal-header">
        <h5 className="modal-title text-center flex" id="staticBackdropLabel">
          Approve On Official Duty
        </h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={handleReset}
        ></button>
      </div>
      <div className="modal-body">
        <div className="px-2 py-4 d-flex flex-column gap-4">
          <div>
            <ResponsiveDataTable
              pagination={false}
              columns={dlSummaryColumns}
              data={tableData}
              aria-label="Request summary"
            />
            <div className="d-flex flex-column timesheet pb-4 gap-4">
              <div className="d-flex">
                <h2 className="label fs-6">Timesheet</h2>
                <div
                  className="timesheet-total-time d-flex gap-2 align-items-center justify-content-center"
                  style={{ backgroundColor: "#e5e9f1" }}
                >
                  <span>Total Time</span>
                  <div className="d-flex">
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ gap: "2px" }}
                    >
                      <span>{totalTime.hours}</span>
                      <span>Hrs</span>
                    </div>
                    {totalTime.minutes > 0 && (
                      <>
                        <span className="d-flex align-items-center justify-content-center px-2">
                          :
                        </span>
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{ gap: "2px" }}
                        >
                          <span>{totalTime.minutes}</span>
                          <span>Mins</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <ViewOODTimesheet
                timesheet={viewTimesheetForViewOOD}
                setTotalTime={setTotalTime}
              />
            </div>
          </div>
          <div>
            <div className="row mt-3">
              <div className="col-md-2"></div>
              <div className="col-md-4">
                <label htmlFor="action" className="has-asterisk">
                  Action
                </label>
              </div>
              <div className="col-md-4">
                <select
                  id="action"
                  className="border"
                  required
                  name="action"
                  value={formData.action}
                  onChange={handleInputChange}
                >
                  <option value="">Select Action</option>
                  <option value="APPROVED">Approve</option>
                  <option value="REJECTED">Reject</option>
                </select>
              </div>
              <div className="col-md-2"></div>
            </div>
            <div className="row mt-3">
              <div className="col-md-2"></div>
              <div className="col-md-4">
                <label htmlFor="comment" className="has-asterisk">
                  Comment
                </label>
              </div>
              <div className="col-md-4">
                <textarea
                  value={formData.comment}
                  name="comment"
                  rows={3}
                  cols={5}
                  required
                  className="border"
                  onChange={handleInputChange}
                ></textarea>
                {errors.comment && (
                  <small className="text-danger">{errors.comment}</small>
                )}
              </div>
              <div className="col-md-2"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="justify-content-center modal-footer">
        <button
          className={isLoading ? "theme-button disabled " : "theme-button "}
          type="submit"
        >
          {isLoading ? (
            <ThreeDots
              height="25"
              width="80"
              radius="9"
              color="white"
              ariaLabel="three-dots-loading"
              wrapperStyle={{}}
              wrapperClassName=""
              visible={true}
            />
          ) : (
            "Submit"
          )}
        </button>
        <button
          type="button"
          className="theme-button bg-grey mx-3 w-35 cancel-modal"
          data-bs-dismiss="modal"
          onClick={handleReset}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ApproveOODModal;
